import { useEffect, useMemo, useState } from 'react';
import { createSavedGaliPlayer, createTempGaliPlayer, fetchSavedGaliPlayers, fetchTempGaliPlayers, searchGaliPlayers } from '../../../services/galiService';
import { canAddPlayerToTeam } from '../utils/galiValidation';
import { buildPlayerRef, normalizePickerPlayer, playerRefKey } from '../utils/galiPlayerRefs';
import { listFromPayload, playerName } from '../utils/galiDisplay';
import GaliPlayerBadge from './GaliPlayerBadge';

const sectionKeys = {
  saved: ['players', 'savedPlayers', 'items', 'data.players', 'data.items'],
  temp: ['players', 'tempPlayers', 'items', 'data.players', 'data.items'],
  registered: ['players', 'registeredPlayers', 'items', 'data.players', 'data.items'],
};

const ResultRow = ({ player, onAdd, teamARefs, teamBRefs, commonRef }) => {
  const ref = player.ref || buildPlayerRef(player);
  const rules = {
    teamA: canAddPlayerToTeam({ ref, teamA: teamARefs, teamB: teamBRefs, commonPlayer: commonRef, target: 'teamA' }),
    teamB: canAddPlayerToTeam({ ref, teamA: teamARefs, teamB: teamBRefs, commonPlayer: commonRef, target: 'teamB' }),
  };
  return (
    <div className="gali-player-row">
      <span>{playerName(player)} <GaliPlayerBadge type={player.refType === 'player' ? 'registered' : player.refType} /> {(player.tempNumber || player.gcNumber) ? <span className="small text-muted">{player.tempNumber || player.gcNumber}</span> : null}</span>
      <span className="gali-actions">
        <button className="btn btn-sm btn-outline-primary" type="button" disabled={!rules.teamA.allowed} title={rules.teamA.reason} onClick={() => onAdd('teamA', player)}>Team A</button>
        <button className="btn btn-sm btn-outline-primary" type="button" disabled={!rules.teamB.allowed} title={rules.teamB.reason} onClick={() => onAdd('teamB', player)}>Team B</button>
        <button className="btn btn-sm btn-outline-secondary" type="button" disabled={playerRefKey(commonRef) === playerRefKey(ref)} onClick={() => onAdd('common', player)}>Common</button>
      </span>
    </div>
  );
};

const GaliPlayerPicker = ({ onAdd, teamARefs = [], teamBRefs = [], commonRef = null }) => {
  const [query, setQuery] = useState('');
  const [saved, setSaved] = useState([]);
  const [temp, setTemp] = useState([]);
  const [registered, setRegistered] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const loadSavedTemp = async () => {
    setError('');
    try {
      const [savedPayload, tempPayload] = await Promise.all([fetchSavedGaliPlayers({ limit: 20 }), fetchTempGaliPlayers({ limit: 20 })]);
      setSaved(listFromPayload(savedPayload, sectionKeys.saved).map((item) => normalizePickerPlayer(item, 'saved')));
      setTemp(listFromPayload(tempPayload, sectionKeys.temp).map((item) => normalizePickerPlayer(item, 'temp')));
    } catch (loadError) {
      setError(loadError.message || 'Unable to load saved/temp players.');
    }
  };

  useEffect(() => { loadSavedTemp(); }, []);

  useEffect(() => {
    let cancelled = false;
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setRegistered([]);
      return undefined;
    }
    setIsLoading(true);
    searchGaliPlayers(trimmed)
      .then((payload) => { if (!cancelled) setRegistered(listFromPayload(payload, sectionKeys.registered).map((item) => normalizePickerPlayer(item, 'player'))); })
      .catch((searchError) => { if (!cancelled) setError(searchError.message || 'Unable to search players.'); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [query]);

  const typedName = query.trim();
  const allSections = useMemo(() => ([
    ['Saved', saved],
    ['TEMP', temp],
    ['Registered', registered],
  ]), [saved, temp, registered]);

  const createAndAdd = async (type) => {
    if (!typedName) return;
    setIsLoading(true);
    setError('');
    try {
      const payload = type === 'temp' ? await createTempGaliPlayer({ displayName: typedName }) : await createSavedGaliPlayer({ displayName: typedName });
      const created = payload.tempPlayer || payload.savedPlayer || payload.player || payload.item || payload;
      const normalized = normalizePickerPlayer(created, type);
      if (type === 'temp') setTemp((prev) => [normalized, ...prev]);
      if (type === 'saved') setSaved((prev) => [normalized, ...prev]);
      onAdd?.('teamA', normalized);
    } catch (createError) {
      setError(createError.message || 'Unable to create player.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="gali-card">
      <h2 className="h5">Find players</h2>
      <input className="form-control" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search saved, temp, or registered players" aria-label="Search Gali players" />
      <div className="gali-actions mt-2">
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={!typedName || isLoading} onClick={() => createAndAdd('temp')}>Create TEMP from typed name</button>
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={!typedName || isLoading} onClick={() => createAndAdd('saved')}>Create Saved from typed name</button>
      </div>
      {error ? <div className="alert alert-warning mt-3 mb-0">{error}</div> : null}
      {isLoading ? <div className="alert alert-info mt-3 mb-0">Loading players…</div> : null}
      <div className="d-grid gap-3 mt-3">
        {allSections.map(([title, players]) => (
          <div key={title}>
            <h3 className="h6">{title}</h3>
            <div className="gali-player-list">
              {players.length ? players.map((player) => <ResultRow key={`${title}-${player.refKey}`} player={player} onAdd={onAdd} teamARefs={teamARefs} teamBRefs={teamBRefs} commonRef={commonRef} />) : <p className="small text-muted mb-0">No {title.toLowerCase()} results.</p>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
export default GaliPlayerPicker;
